import { useMemo, useReducer } from 'react';

type Action<A, K extends keyof A = keyof A> = {
  type: K;
  args: A[K] extends (...args: infer P) => unknown ? P : never;
};

type Reducer<S, A> = {
  [K in keyof A]: A[K] extends (...args: infer P) => unknown ? (this: S, ...args: P) => S | null | undefined : never;
};

export function useActions<S, A>(initial: S, actions: Reducer<S, A>): [S, A] {
  const [state, dispatch] = useReducer(
    (s: S, a: Action<A>): S => actions[a.type]?.apply(s, a.args as never) ?? s,
    initial,
  );

  const map = useMemo(
    () =>
      Object.fromEntries(
        Object.keys(actions).map((type) => [
          type,
          (...args: never) =>
            dispatch({
              type: type as keyof A,
              args,
            }),
        ]),
      ),
    // Keeps the memo stable even if the object changes, as long as the keys are the same
    // eslint-disable-next-line react-hooks/exhaustive-deps
    Object.keys(actions),
  );

  return [state, map as never];
}
